import React, { useEffect, useState } from 'react';
import { getAttempt } from '@/Data/Topik/Attempt';
import { getQuestionnaire } from '@/Data/Topik/Questionnaire';
import type { AttemptWithAnswers, QuestionnaireResponse, MultipleChoiceQuestion } from '@/Data/Topik/types';
import { MultipleChoiceQuestion as MCQComponent } from '@/Components/Topik/MultipleChoiceQuestion';
import './index.scss';

const questionidToDisplay = (questionid: number) => {
    if (questionid <= 50) {
        return `L${questionid}`;
    } else if (questionid <= 100) {
        return `R${questionid - 50}`;
    } else {
        return `5${questionid - 100}`;
    }
};


interface TopikResultProps {
    questionnaireid: string;
    attemptid: string;
    section: 'listening' | 'reading' | 'writing';
}

function TopikResult({ questionnaireid, attemptid, section }: TopikResultProps) {
    const [attempt, setAttempt] = useState<AttemptWithAnswers | null>(null);
    const [questionnaire, setQuestionnaire] = useState<QuestionnaireResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [playingAudio, setPlayingAudio] = useState<string | null>(null);
    const audioRef = React.useRef<HTMLAudioElement | null>(null);
    const [activeQuestionId, setActiveQuestionId] = useState<number | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                
                // Fetch attempt data with answers
                const attemptData = await getAttempt(attemptid);
                if (!attemptData) {
                    setError('Failed to load attempt data');
                    return;
                }
                setAttempt(attemptData);

                // Fetch questionnaire data
                const questionnaireData = await getQuestionnaire({ questionnaireid, section });
                if (!questionnaireData) {
                    setError('Failed to load questionnaire');
                    return;
                }
                setQuestionnaire(questionnaireData);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Error loading result data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [questionnaireid, attemptid, section]);

    const handlePlayAudio = (audioUrl: string) => {
        if (playingAudio === audioUrl) {
            // Stop the current audio
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
            setPlayingAudio(null);
        } else {
            // Stop any currently playing audio
            if (audioRef.current) {
                audioRef.current.pause();
            }
            
            // Play the new audio
            const audio = new Audio(audioUrl);
            audioRef.current = audio;
            
            audio.onended = () => {
                setPlayingAudio(null);
                audioRef.current = null;
            };
            
            audio.onerror = () => {
                setPlayingAudio(null);
                audioRef.current = null;
                alert('Failed to play audio');
            };
            
            audio.play().catch(() => {
                setPlayingAudio(null);
                audioRef.current = null;
                alert('Failed to play audio');
            });
            
            setPlayingAudio(audioUrl);
        }
    };

    // Cleanup audio on unmount
    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    // Get question groups based on section
    const questionGroups = React.useMemo(() => {
        if (!questionnaire) return [];
        return section === 'listening' ? questionnaire.listeningQuestionGroups :
               section === 'reading' ? questionnaire.readingQuestionGroups :
               questionnaire.writingQuestionGroups;
    }, [questionnaire, section]);

    // Scroll listener to highlight active question
    useEffect(() => {
        const handleScroll = () => {
            if (!questionnaire) return;
            
            const scrollPosition = window.scrollY + 100; // offset for better detection
            let currentActiveId: number | null = null;
            
            // Get all questions from question groups
            const allQuestions: number[] = [];
            questionGroups.forEach(group => {
                group.questions.forEach(q => allQuestions.push(q.questionid));
            });
            
            // Find which question is currently visible
            for (const questionId of allQuestions) {
                const element = document.getElementById(`question-${questionId}`);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    const elementTop = rect.top + window.scrollY;
                    
                    if (scrollPosition >= elementTop) {
                        currentActiveId = questionId;
                    }
                }
            }
            
            setActiveQuestionId(currentActiveId);
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Call once to set initial state
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [questionnaire, questionGroups]);

    if (loading) {
        return (
            <div className="TopikResult">
                <div className="LoadingView">
                    <div className="LoadingSpinner"></div>
                    <p>Loading results...</p>
                </div>
            </div>
        );
    }

    if (error || !attempt || !questionnaire) {
        return (
            <div className="TopikResult">
                <div className="ErrorView">
                    <h3>Error</h3>
                    <p>{error || 'Failed to load results'}</p>
                </div>
            </div>
        );
    }

    // Get section score from attempt
    const sectionScore = section === 'listening' ? attempt.listening_score :
                        section === 'reading' ? attempt.reading_score :
                        attempt.writing_score;

    // Format date
    const testDate = new Date(attempt.finished_at ? attempt.finished_at * 1000 : Date.now());
    const formattedDate = testDate.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    });

    // Helper function to scroll to question
    const scrollToQuestion = (questionId: number) => {
        const element = document.getElementById(`question-${questionId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="TopikResult">
            {/* Floating Navigation */}
            <div className="FloatingNavigation">
                <div className="NavigationHeader">Questions</div>
                <div className="NavigationList">
                    {questionGroups.map((group, groupIndex) => (
                        <div key={groupIndex} className="NavigationGroup">
                            <div className="GroupHeader">
                                {group.questions.length > 0 && (
                                    `${questionidToDisplay(group.questions[0].questionid)}${group.questions.length > 1 ? `~${questionidToDisplay(group.questions[group.questions.length-1].questionid)}` : ''}`
                                )}
                            </div>
                            <div className="QuestionList">
                                {group.questions.map((question) => {
                                    const userAnswer = attempt.answers?.find(a => a.questionid === question.questionid);
                                    const isCorrect = userAnswer?.is_correct || false;
                                    
                                    return (
                                        <div 
                                            key={question.questionid}
                                            className={`QuestionItem ${isCorrect ? 'correct' : 'incorrect'} ${activeQuestionId === question.questionid ? 'active' : ''}`}
                                            onClick={() => scrollToQuestion(question.questionid)}
                                        >
                                            <span className="QuestionNumber">{questionidToDisplay(question.questionid)}</span>
                                            <span className={`StatusIcon ${isCorrect ? 'correct' : 'incorrect'}`}>
                                                {isCorrect ? '✓' : '✗'}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="BodyContainer">
                {/* Test Info Component */}
                <div className="TestInfo">
                    {/* Header Row: Logo + Section + View Answer */}
                    <div className="HeaderRow">
                        <div className="Logo">
                            <img src="/img/mate-bg-logo.svg" alt="Mate Logo" />
                        </div>
                        <div className="SectionName">
                            {section.charAt(0).toUpperCase() + section.slice(1)}
                        </div>
                        <div className="ViewAnswer">
                            View Answer & Explanation
                        </div>
                    </div>
                    
                    {/* Test Description */}
                    <div className="TestDescription">
                        Mate Korean language proficiency test EXPERT - {section.charAt(0).toUpperCase() + section.slice(1)}
                    </div>
                    
                    {/* Score */}
                    <div className="Score">
                        {`score : ${sectionScore || 0} (${attempt.answers?.filter(a => a.is_correct).length || 0} correct answers over ${attempt.answers?.length || 0} questions)`}
                    </div>
                    
                    {/* Test Date */}
                    <div className="TestDate">
                        Test date : {formattedDate}
                    </div>
                </div>

                {/* Questions and Solutions */}
                {questionGroups.map((group, groupIndex) => (
                    <div key={groupIndex} className="QuestionGroup">
                        {group.shared_question_text && (
                            <div className="SampleQuestionInstruction">
                                {group.questions.length > 0 
                                    ?`[${questionidToDisplay(group.questions[0].questionid)}${group.questions.length > 1?`~${questionidToDisplay(group.questions[group.questions.length-1].questionid)}`:''}]`
                                    :''}  {group.shared_question_text}
                            </div>
                        )}
                        
                        {group.shared_resource?.type === 'text' && group.shared_resource?.text && (
                            <div className="SharedText">
                                {group.shared_resource.text}
                            </div>
                        )}

                        {group.shared_resource?.type === 'audio' && group.shared_resource?.audio_script?.length > 0 && (
                            <div className="AudioScript">
                                <div className="AudioScriptHeader">
                                    <span>🎧 Audio Script</span>
                                    <button 
                                        className="PlayButton"
                                        onClick={() => handlePlayAudio((group.shared_resource as any).audio_url)}
                                    >
                                        {playingAudio === (group.shared_resource as any).audio_url ? '⏸️' : '▶️'}
                                    </button>
                                </div>
                                {group.shared_resource.audio_script.map((script, idx) => (
                                    <div key={idx} className="SpeakerLine">
                                        <span className="SpeakerInfo">{script.role}:</span>
                                        <span className="SpeakerText">{script.text}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="SampleQuestionContainer">
                            <div className="SampleQuestionBox">
                                {group.questions.map((question, qIndex) => {
                                    const userAnswer = attempt.answers?.find(a => a.questionid === question.questionid);
                                    const isCorrect = userAnswer?.is_correct || false;
                                    
                                    return (
                                        <React.Fragment key={question.questionid}>
                                            {qIndex > 0 && <div className="Divider"></div>}
                                            <div className="SampleQuestionSub" id={`question-${question.questionid}`}>
                                                <div className="QuestionTitle">
                                                    <span className="QuestionNumber">{questionidToDisplay(question.questionid)}.</span>
                                                    {question.text && question.text.length <= 50 && (
                                                        <span className="QuestionText">{question.text}</span>
                                                    )}
                                                </div>

                                                {question.text && question.text.length > 50 && (
                                                    <div className="LongQuestionText">
                                                        {question.text.split('\n\n').map((text, idx) => (
                                                            <div key={idx} style={{
                                                                background: '#f5f5f5',
                                                                borderRadius: '12px',
                                                                padding: '24px',
                                                                border: '1px solid black',
                                                                fontSize: '18px',
                                                                fontWeight: 400,
                                                                lineHeight: '1.8',
                                                                letterSpacing: '0.2px',
                                                                marginTop: idx > 0 ? '12px' : '16px',
                                                                marginBottom: '16px',
                                                                whiteSpace: 'pre-wrap',
                                                            }}>
                                                                {text}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {question.image && (
                                                    <div className="QuestionImage">
                                                        {question.image.endsWith('.html') ? (
                                                            <iframe 
                                                                src={question.image}
                                                                width="100%"
                                                                height="320"
                                                                frameBorder="0"
                                                                title={`Question ${questionidToDisplay(question.questionid)}`}
                                                                style={{ borderRadius: '8px' }}
                                                            />
                                                        ) : (
                                                            <img 
                                                                src={question.image}
                                                                alt={`Question ${questionidToDisplay(question.questionid)}`}
                                                                style={{ 
                                                                    maxWidth: '100%',
                                                                    height: 'auto',
                                                                    borderRadius: '8px'
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                )}

                                                {question.audio?.audio_script && (
                                                    <div className="AudioScript">
                                                        <div className="AudioScriptHeader">
                                                            <span>🎧 Audio Script</span>
                                                            <button 
                                                                className="PlayButton"
                                                                onClick={() => handlePlayAudio(question.audio!.audio_url)}
                                                            >
                                                                {playingAudio === question.audio.audio_url ? '⏸️' : '▶️'}
                                                            </button>
                                                        </div>
                                                        {question.audio.audio_script.map((script, idx) => (
                                                            <div key={idx} className="SpeakerLine">
                                                                <span className="SpeakerInfo">{script.role}:</span>
                                                                <span className="SpeakerText">{script.text}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {(question as MultipleChoiceQuestion).choices && (
                                                    <div style={{ pointerEvents: 'none' }}>
                                                        <MCQComponent
                                                            question={question as MultipleChoiceQuestion}
                                                            selectedAnswer={userAnswer?.answer}
                                                            onAnswerSelect={() => {}}
                                                            showCorrectAnswer={true}
                                                        />
                                                    </div>
                                                )}

                                                {(question.question_type === 'complete-dialogue' || question.question_type === 'complete-sentence'
                                                || question.question_type === 'essay-writing' || question.question_type === 'data-description') && (
                                                    <div className="TextResult">
                                                        {(question.question_type === 'complete-dialogue' || question.question_type === 'complete-sentence') && ["ㄱ", "ㄴ"] ? (
                                                            // Multiple blanks
                                                            <>
                                                                <div className="UserAnswerText">
                                                                    <strong>Your answers:</strong>
                                                                    {["ㄱ", "ㄴ"].map((blank, idx) => (
                                                                        <div key={idx} style={{ marginLeft: '20px', marginTop: '4px' }}>
                                                                            {userAnswer?.answer?.split('\n')[idx] || 'No answer'}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                <div className="CorrectAnswerText">
                                                                    <strong>Correct answers:</strong>
                                                                    {["ㄱ", "ㄴ"].map((blank, idx) => (
                                                                        <div key={idx} style={{ marginLeft: '20px', marginTop: '4px' }}>
                                                                            {question.correct_answer?.split('\n')[idx] || ""}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            // Single answer
                                                            <>
                                                                <div className="UserAnswerText">
                                                                    <strong>Your answer:</strong> {userAnswer?.answer || 'No answer'}
                                                                </div>
                                                                {question.question_type !== 'essay-writing' && question.question_type !== 'data-description' && (
                                                                    <div className="CorrectAnswerText">
                                                                        <strong>Correct answer:</strong> {question.correct_answer}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Solution Component */}
                                            <div className={`SolutionContainer ${!isCorrect ? 'incorrect' : ''}`}>
                                                <div className="SolutionHeader">
                                                    <div className="SolutionIcon">
                                                        {isCorrect ? (
                                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <circle cx="12" cy="12" r="10" fill="#4CAF50"/>
                                                                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            </svg>
                                                        ) : (
                                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <circle cx="12" cy="12" r="10" fill="#F44336"/>
                                                                <path d="M8 8l8 8M16 8l-8 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <div className="SolutionTitle">
                                                        <div className="SolutionNumber">{questionidToDisplay(question.questionid)}.</div>
                                                    </div>
                                                </div>
                                                <div className="SolutionContent">
                                                    {userAnswer?.feedback ? (
                                                        <p>{userAnswer.feedback}</p>
                                                    ) : (
                                                        <p>
                                                            {isCorrect 
                                                                ? `Correct! The answer is "${question.correct_answer}".`
                                                                : `The correct answer is "${question.correct_answer}". ${
                                                                    userAnswer?.answer 
                                                                        ? `You answered "${userAnswer.answer}".` 
                                                                        : 'You did not answer this question.'
                                                                }`
                                                            }
                                                        </p>
                                                    )}
                                                    {question.explanation && (
                                                        <div className="Explanation">
                                                            <strong>Explanation:</strong>
                                                            <p>{question.explanation}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default TopikResult;